import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';

import { ReportService } from '../../services/reportService';
import {
  DigestDelivery,
  DigestDeliveryStatus as Status,
  DIGEST_STATUS_DESCRIPTION,
} from '../../types/DigestDelivery';

/**
 * Delivery history for the scheduled QA digest.
 *
 * The reason this screen exists is to make a digest that did *not* go out visible to the people
 * expecting it. A missing report announces nothing by itself, which is the failure this whole
 * feature was built to close — surfacing it only in an operator's log would leave the agency
 * assuming a quiet week.
 */
export const DigestDeliveryStatusPanel: React.FC = () => {
  const [deliveries, setDeliveries] = useState<DigestDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setDeliveries(await ReportService.getDigestDeliveries());
      setFailed(false);
    } catch (_err) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading digest delivery history" />
      </Box>
    );
  }

  if (failed) {
    return <Alert severity="error">Could not load digest delivery history.</Alert>;
  }

  const unresolved = deliveries.filter(
    (d) => d.status === 'Failed' || d.status === 'Indeterminate'
  );

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        Scheduled Digest Delivery
      </Typography>

      {deliveries.length === 0 && (
        <Alert severity="info">
          <AlertTitle>No digests have been sent yet</AlertTitle>
          A digest is sent once a reporting period closes, if the schedule is switched on for this
          agency. Contact GovIQ support to set up or change the distribution list.
        </Alert>
      )}

      {unresolved.length > 0 && (
        // Alert rather than coloured text: palette.error.main is hardcoded for both palette modes
        // and fails contrast on the dark paper, so small error-coloured text is unreadable for
        // half the users. Alert derives its colour per mode.
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            {unresolved.length} recent {unresolved.length === 1 ? 'period' : 'periods'} did not
            complete
          </AlertTitle>
          A period marked <strong>Indeterminate</strong> was started but never confirmed, so it is
          not known whether it was delivered — it is not re-sent automatically, to avoid sending a
          chief the same report twice. Check with the recipients before re-sending manually.
        </Alert>
      )}

      {deliveries.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Scheduled digest delivery history">
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Recipients</TableCell>
                <TableCell align="right">Attempts</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveries.map((d) => (
                <TableRow key={`${d.periodStart}-${d.periodEnd}`}>
                  <TableCell>
                    {d.periodStart === d.periodEnd
                      ? d.periodStart
                      : `${d.periodStart} to ${d.periodEnd}`}
                  </TableCell>
                  <TableCell>
                    {/* Text carries the meaning; colour only reinforces it. */}
                    <Chip
                      size="small"
                      label={d.status}
                      color={statusColor(d.status)}
                      variant={d.status === 'Sent' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">{d.recipientCount}</TableCell>
                  <TableCell align="right">{d.attemptCount}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {d.failureCategory
                        ? `${DIGEST_STATUS_DESCRIPTION[d.status]} (${d.failureCategory})`
                        : DIGEST_STATUS_DESCRIPTION[d.status]}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        Periods are calendar days in the agency&apos;s timezone. Recipient addresses are not shown
        here.
      </Typography>
    </Box>
  );
};

function statusColor(status: Status): 'success' | 'error' | 'warning' | 'default' {
  switch (status) {
    case 'Sent':
      return 'success';
    case 'Failed':
      return 'error';
    case 'Indeterminate':
      return 'warning';
    default:
      return 'default';
  }
}
